// Общее ядро расчётов Eta/Theta.
//
// Используется edge-функциями (eta-spread-monitor, theta-executor, theta-positions-monitor)
// и фронтендом (src/lib/etaScenarios.ts). Модуль ЧИСТЫЙ: без импортов, без Deno/DOM API,
// без сети — только арифметика, поэтому его можно покрыть unit-тестами (etaCore.test.ts)
// и импортировать из Vite.
//
// Раньше одни и те же формулы (комиссии, маржа, сценарии) были написаны дважды — в функции
// и во фронте — и расходились.

// ─────────────────────────────────────────────────────────────────────────────
// Символы и экспирация
// ─────────────────────────────────────────────────────────────────────────────

export interface ParsedSymbol {
  underlying: string;
  expiry: string; // YYYYMMDD
  strike: number;
  optType: 'C' | 'P';
}

/** Canonical (Gate) символ: BTC_USDT-20260506-80000-C. */
export function parseCanonical(symbol: string): ParsedSymbol | null {
  const parts = symbol.split('-');
  if (parts.length < 4) return null;
  const baseRaw = parts[0];
  const underlying = baseRaw.includes('_') ? baseRaw.split('_')[0] : baseRaw;
  const expiry = parts[1];
  if (!/^\d{8}$/.test(expiry)) return null;
  const strike = Number(parts[2]);
  const optTypeRaw = parts[3];
  if (!['C', 'P'].includes(optTypeRaw) || !Number.isFinite(strike) || strike <= 0) return null;
  return { underlying, expiry, strike, optType: optTypeRaw as 'C' | 'P' };
}

/** Опционы основных бирж экспирируются в 08:00 UTC. */
export const EXPIRY_HOUR_UTC = 8;

export function expiryMs(expiryYYYYMMDD: string): number {
  return Date.UTC(
    Number(expiryYYYYMMDD.slice(0, 4)),
    Number(expiryYYYYMMDD.slice(4, 6)) - 1,
    Number(expiryYYYYMMDD.slice(6, 8)),
    EXPIRY_HOUR_UTC,
  );
}

export function daysUntilExpiry(expiryYYYYMMDD: string, nowMs: number): number {
  return (expiryMs(expiryYYYYMMDD) - nowMs) / 86_400_000;
}

// ─────────────────────────────────────────────────────────────────────────────
// Конфигурация бирж (таблица exchange_fees)
// ─────────────────────────────────────────────────────────────────────────────

export interface FeeConfig {
  /** Taker-комиссия как доля цены базового актива. */
  takerFeePct: number;
  /** Потолок taker-комиссии как доля премии. */
  takerFeePremiumCapPct: number | null;
  /** Комиссия поставки/расчёта на экспирации как доля цены базового актива. */
  settlementPct: number;
  shortImPctSpot: number;
  shortImMinPctSpot: number;
  shortImOtmFactor: number;
}

export const DEFAULT_TAKER_FEE_PCT = 0.0003;
export const DEFAULT_FEE_PREMIUM_CAP = 0.125;
export const DEFAULT_SETTLEMENT_PCT = 0.00015;
/** Потолок комиссии расчёта как доля внутренней стоимости опциона (Deribit-like). */
export const SETTLEMENT_INTRINSIC_CAP = 0.125;
export const DEFAULT_SHORT_IM_PCT = 0.15;
export const DEFAULT_SHORT_IM_MIN_PCT = 0.10;
export const MIN_APR_DAYS = 1;

/** Биржи, где опционы рассчитываются в монете (BTC/ETH), а не в USDT. */
export const COIN_SETTLED_EXCHANGES = new Set(['deribit', 'okx']);

export function feeConfigFromRow(row: Record<string, unknown>): FeeConfig {
  const num = (v: unknown, d: number): number => {
    const n = Number(v);
    return v != null && Number.isFinite(n) ? n : d;
  };
  return {
    takerFeePct: num(row.taker_fee_pct, DEFAULT_TAKER_FEE_PCT),
    takerFeePremiumCapPct: row.taker_fee_premium_cap_pct != null
      ? num(row.taker_fee_premium_cap_pct, DEFAULT_FEE_PREMIUM_CAP)
      : DEFAULT_FEE_PREMIUM_CAP,
    settlementPct: num(row.settlement_pct, DEFAULT_SETTLEMENT_PCT),
    shortImPctSpot: num(row.short_im_pct_spot, DEFAULT_SHORT_IM_PCT),
    shortImMinPctSpot: num(row.short_im_min_pct_spot, DEFAULT_SHORT_IM_MIN_PCT),
    shortImOtmFactor: num(row.short_im_otm_factor, 1),
  };
}

function isPos(n: number | undefined | null): n is number {
  return n != null && Number.isFinite(n) && n > 0;
}

/**
 * Taker-комиссия за 1 опцион на 1 базовую единицу:
 * taker_fee_pct × spot, но не больше cap × премия. Без spot — консервативно cap × премия.
 */
export function takerFee(cfg: FeeConfig | undefined, premium: number, spot: number | undefined | null): number {
  const pct = cfg?.takerFeePct ?? DEFAULT_TAKER_FEE_PCT;
  const cap = cfg ? cfg.takerFeePremiumCapPct : DEFAULT_FEE_PREMIUM_CAP;
  const premiumCap = cap != null && cap > 0 ? premium * cap : Infinity;
  if (!isPos(spot)) return Number.isFinite(premiumCap) ? premiumCap : premium * DEFAULT_FEE_PREMIUM_CAP;
  return Math.min(pct * spot, premiumCap);
}

export function intrinsicValue(optType: 'C' | 'P', spot: number, strike: number): number {
  return optType === 'C' ? Math.max(spot - strike, 0) : Math.max(strike - spot, 0);
}

/**
 * Комиссия расчёта на экспирации за 1 опцион (если опцион ITM):
 * settlement_pct × spot, но не больше 12.5% внутренней стоимости. OTM — 0.
 * Оценка по текущему spot (будущий spot неизвестен).
 */
export function settlementFee(
  cfg: FeeConfig | undefined,
  optType: 'C' | 'P',
  spot: number | undefined | null,
  strike: number,
): number {
  if (!isPos(spot)) return 0;
  const intrinsic = intrinsicValue(optType, spot, strike);
  if (!(intrinsic > 0)) return 0;
  const pct = cfg?.settlementPct ?? DEFAULT_SETTLEMENT_PCT;
  return Math.min(pct * spot, SETTLEMENT_INTRINSIC_CAP * intrinsic);
}

/**
 * Маржа на продажу 1 опциона (Deribit-like):
 *   call: max(im × S − otm × OTM, min × S) + премия
 *   put:  max(im × S − otm × OTM, min × K) + премия
 */
export function shortMarginPerUnitUsd(
  optType: 'C' | 'P',
  spot: number,
  strike: number,
  premium: number,
  cfg?: FeeConfig,
): number {
  const im = cfg?.shortImPctSpot ?? DEFAULT_SHORT_IM_PCT;
  const imMin = cfg?.shortImMinPctSpot ?? DEFAULT_SHORT_IM_MIN_PCT;
  const otm = cfg?.shortImOtmFactor ?? 1;
  if (optType === 'C') {
    return Math.max(im * spot - otm * Math.max(strike - spot, 0), imMin * spot) + premium;
  }
  return Math.max(im * spot - otm * Math.max(spot - strike, 0), imMin * strike) + premium;
}

// ─────────────────────────────────────────────────────────────────────────────
// Котировки и фильтры
// ─────────────────────────────────────────────────────────────────────────────

export interface ExchangeQuote {
  exchange: string;
  bid: number;
  /** Размер в базовой монете (BTC/ETH). */
  bidSize?: number;
  ask: number;
  askSize?: number;
  spot?: number;
  takenAt?: string;
}

/** Если собственный спред биржи (ask−bid)/mid больше этого — книга неликвидна, котировку не берём. */
export const MAX_OWN_SPREAD_PCT = 60;
/** Сторона котировки с известным размером меньше этой суммы ($) считается мусорной. */
export const MIN_SIDE_NOTIONAL_USD = 5;

export type QuoteRejectReason = 'own_spread' | 'empty';

/**
 * Чистит котировку: отбрасывает стороны с ничтожным объёмом и целиком котировки с
 * огромным собственным спредом (типичный источник ложных «арбитражей» в пустых книгах).
 */
export function sanitizeQuote(q: ExchangeQuote): { quote: ExchangeQuote | null; reason?: QuoteRejectReason } {
  let bid = isPos(q.bid) ? q.bid : 0;
  let ask = isPos(q.ask) ? q.ask : 0;
  const bidSize = isPos(q.bidSize) ? q.bidSize : undefined;
  const askSize = isPos(q.askSize) ? q.askSize : undefined;
  if (bid > 0 && bidSize != null && bid * bidSize < MIN_SIDE_NOTIONAL_USD) bid = 0;
  if (ask > 0 && askSize != null && ask * askSize < MIN_SIDE_NOTIONAL_USD) ask = 0;
  if (bid > 0 && ask > 0) {
    const mid = (bid + ask) / 2;
    if ((ask - bid) / mid * 100 > MAX_OWN_SPREAD_PCT) return { quote: null, reason: 'own_spread' };
  }
  if (!(bid > 0) && !(ask > 0)) return { quote: null, reason: 'empty' };
  return { quote: { ...q, bid, ask, bidSize, askSize } };
}

// ─────────────────────────────────────────────────────────────────────────────
// Спреды между биржами
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadResult {
  minBid: number; minBidExch: string; minBidSpot?: number; minBidSize?: number;
  maxBid: number; maxBidExch: string; maxBidSpot?: number; maxBidSize?: number;
  bidSpreadPct: number;
  minAsk: number; minAskExch: string; minAskSpot?: number; minAskSize?: number;
  maxAsk: number; maxAskExch: string; maxAskSpot?: number; maxAskSize?: number;
  askSpreadPct: number;
  /** Реальный межбиржевой арбитраж: (bid на X − ask на Y) / ask, X ≠ Y. */
  crossSpreadPct: number;
  crossBuyExch: string;
  crossSellExch: string;
}

export function calcSpread(quotes: ExchangeQuote[]): SpreadResult | null {
  const bids = quotes.filter((q) => q.bid > 0);
  const asks = quotes.filter((q) => q.ask > 0);
  if (bids.length < 2 && asks.length < 2 && !(bids.length >= 1 && asks.length >= 1)) return null;

  let minBid = Infinity, minBidExch = '', minBidSpot: number | undefined, minBidSize: number | undefined;
  let maxBid = -Infinity, maxBidExch = '', maxBidSpot: number | undefined, maxBidSize: number | undefined;
  for (const q of bids) {
    if (q.bid < minBid) { minBid = q.bid; minBidExch = q.exchange; minBidSpot = q.spot; minBidSize = q.bidSize; }
    if (q.bid > maxBid) { maxBid = q.bid; maxBidExch = q.exchange; maxBidSpot = q.spot; maxBidSize = q.bidSize; }
  }
  let minAsk = Infinity, minAskExch = '', minAskSpot: number | undefined, minAskSize: number | undefined;
  let maxAsk = -Infinity, maxAskExch = '', maxAskSpot: number | undefined, maxAskSize: number | undefined;
  for (const q of asks) {
    if (q.ask < minAsk) { minAsk = q.ask; minAskExch = q.exchange; minAskSpot = q.spot; minAskSize = q.askSize; }
    if (q.ask > maxAsk) { maxAsk = q.ask; maxAskExch = q.exchange; maxAskSpot = q.spot; maxAskSize = q.askSize; }
  }

  let crossSpreadPct = 0, crossBuyExch = '', crossSellExch = '';
  for (const buy of asks) {
    for (const sell of bids) {
      if (buy.exchange === sell.exchange) continue;
      const pct = (sell.bid - buy.ask) / buy.ask * 100;
      if (pct > crossSpreadPct) { crossSpreadPct = pct; crossBuyExch = buy.exchange; crossSellExch = sell.exchange; }
    }
  }

  const hasBids = bids.length >= 2;
  const hasAsks = asks.length >= 2;
  return {
    minBid: hasBids ? minBid : 0, minBidExch: hasBids ? minBidExch : '',
    minBidSpot: hasBids ? minBidSpot : undefined, minBidSize: hasBids ? minBidSize : undefined,
    maxBid: hasBids ? maxBid : 0, maxBidExch: hasBids ? maxBidExch : '',
    maxBidSpot: hasBids ? maxBidSpot : undefined, maxBidSize: hasBids ? maxBidSize : undefined,
    bidSpreadPct: hasBids && minBid > 0 ? (maxBid - minBid) / minBid * 100 : 0,
    minAsk: hasAsks ? minAsk : 0, minAskExch: hasAsks ? minAskExch : '',
    minAskSpot: hasAsks ? minAskSpot : undefined, minAskSize: hasAsks ? minAskSize : undefined,
    maxAsk: hasAsks ? maxAsk : 0, maxAskExch: hasAsks ? maxAskExch : '',
    maxAskSpot: hasAsks ? maxAskSpot : undefined, maxAskSize: hasAsks ? maxAskSize : undefined,
    askSpreadPct: hasAsks && minAsk > 0 ? (maxAsk - minAsk) / minAsk * 100 : 0,
    crossSpreadPct, crossBuyExch, crossSellExch,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Сценарии арбитража
// ─────────────────────────────────────────────────────────────────────────────

export interface CapitalLeg {
  exchange: string;
  role: 'buy' | 'sell' | 'spot';
  usd: number;
  note: string;
}

export interface ScenarioResult {
  buyExch: string;
  sellExch: string;
  buyAsk: number;
  buyAskSizeBase?: number;
  buyAskSizeEstimated?: boolean;
  sellBid: number;
  sellBidSizeBase?: number;
  sellBidSizeEstimated?: boolean;
  qtyBase: number;
  grossUsd: number;
  feesUsd: number;
  /** Оценка комиссии расчёта на экспирации (только with_short, держим до экспирации). */
  settlementFeesUsd?: number;
  netUsd: number;
  netPctOfPremium: number;
  capitalRequiredUsd?: number;
  capitalLegs?: CapitalLeg[];
  premiumLockUsd?: number;
  marginLockUsd?: number;
  totalCapitalUsd?: number;
  roiOnCapitalPct?: number;
  aprPct?: number;
  liquidationPriceUsd?: number;
  /** Одна из ног рассчитывается в монете (Deribit/OKX) — PnL в USD плавает вместе с курсом. */
  coinSettledLeg?: boolean;
  warnings?: string[];
}

export interface ScenarioPair {
  noShort: ScenarioResult | null;
  withShort: ScenarioResult | null;
}

const DEFAULT_QTY_BASE = 1;

export function computeScenarios(
  quotes: ExchangeQuote[],
  fees: Map<string, FeeConfig>,
  spot: number,
  strike: number,
  optType: 'C' | 'P',
  daysToExpiry: number,
): ScenarioPair {
  let bestNet: ScenarioResult | null = null;
  let bestApr: ScenarioResult | null = null;
  const hasValidSpot = isPos(spot);
  for (const buy of quotes) {
    if (!(buy.ask > 0)) continue;
    for (const sell of quotes) {
      if (buy.exchange === sell.exchange) continue;
      if (!(sell.bid > 0)) continue;

      const warnings: string[] = [];
      const buySize = isPos(buy.askSize) ? buy.askSize : null;
      const sellSize = isPos(sell.bidSize) ? sell.bidSize : null;
      if (buySize == null && sellSize == null) {
        warnings.push(`Размеры стакана недоступны: использован условный размер ${DEFAULT_QTY_BASE}.`);
      } else if (buySize == null) {
        warnings.push(`Размер на бирже ${buy.exchange.toUpperCase()} недоступен - взят размер противоположной ноги.`);
      } else if (sellSize == null) {
        warnings.push(`Размер на бирже ${sell.exchange.toUpperCase()} недоступен - взят размер противоположной ноги.`);
      }
      const coinSettled = COIN_SETTLED_EXCHANGES.has(buy.exchange.toLowerCase())
        || COIN_SETTLED_EXCHANGES.has(sell.exchange.toLowerCase());
      if (coinSettled) {
        warnings.push('Одна из ног рассчитывается в монете (Deribit/OKX): результат в USD зависит от курса, а цена расчёта берётся по разным индексам бирж (базисный риск).');
      }

      const qty = buySize != null && sellSize != null ? Math.min(buySize, sellSize) : (buySize ?? sellSize ?? DEFAULT_QTY_BASE);
      const buyCfg = fees.get(buy.exchange.toLowerCase());
      const sellCfg = fees.get(sell.exchange.toLowerCase());
      const spotRef = hasValidSpot ? spot : undefined;
      const feesTotal = (takerFee(buyCfg, buy.ask, spotRef) + takerFee(sellCfg, sell.bid, spotRef)) * qty;
      const gross = (sell.bid - buy.ask) * qty;
      const net = gross - feesTotal;
      const premiumLock = buy.ask * qty;
      const netPctOfPremium = premiumLock > 0 ? net / premiumLock * 100 : 0;

      // Сценарий 1: без шорта (закрытие существующего лонга)
      const r1: ScenarioResult = {
        buyExch: buy.exchange,
        sellExch: sell.exchange,
        buyAsk: buy.ask,
        buyAskSizeBase: buySize ?? qty,
        buyAskSizeEstimated: buySize == null,
        sellBid: sell.bid,
        sellBidSizeBase: sellSize ?? qty,
        sellBidSizeEstimated: sellSize == null,
        qtyBase: qty,
        grossUsd: gross,
        feesUsd: feesTotal,
        netUsd: net,
        netPctOfPremium,
        capitalRequiredUsd: premiumLock,
        capitalLegs: [{ exchange: buy.exchange, role: 'buy', usd: premiumLock, note: 'премия' }],
        coinSettledLeg: coinSettled,
        warnings: warnings.length > 0 ? [...warnings] : undefined,
      };
      if (!bestNet || r1.netUsd > bestNet.netUsd) bestNet = r1;

      // Сценарий 2: со шортом — держим до экспирации, платим расчёт по обеим ногам.
      const shortWarnings = [...warnings];
      const spotForShort = hasValidSpot ? spot : Math.max((buy.ask + sell.bid) / 2, buy.ask, sell.bid, 1);
      if (!hasValidSpot) shortWarnings.push('Spot недоступен: использован mid из опционных котировок для margin/APR.');
      const settlementFees = (settlementFee(buyCfg, optType, spotRef, strike) + settlementFee(sellCfg, optType, spotRef, strike)) * qty;
      const net2 = net - settlementFees;
      const marginLock = shortMarginPerUnitUsd(optType, spotForShort, strike, sell.bid, sellCfg) * qty;
      const totalCap = premiumLock + marginLock;
      const roiCap = totalCap > 0 ? net2 / totalCap * 100 : 0;
      const normalizedDays = Number.isFinite(daysToExpiry) ? Math.max(daysToExpiry, MIN_APR_DAYS) : MIN_APR_DAYS;
      const apr = roiCap * (365 / normalizedDays);
      const liqBuffer = sellCfg?.shortImPctSpot ?? DEFAULT_SHORT_IM_PCT;
      const liqPx = optType === 'C' ? spotForShort * (1 + liqBuffer) : Math.max(0, spotForShort * (1 - liqBuffer));

      const r2: ScenarioResult = {
        ...r1,
        settlementFeesUsd: settlementFees,
        netUsd: net2,
        netPctOfPremium: premiumLock > 0 ? net2 / premiumLock * 100 : 0,
        capitalRequiredUsd: totalCap,
        capitalLegs: [
          { exchange: buy.exchange, role: 'buy', usd: premiumLock, note: 'премия' },
          { exchange: sell.exchange, role: 'sell', usd: marginLock, note: 'маржа' },
        ],
        premiumLockUsd: premiumLock,
        marginLockUsd: marginLock,
        totalCapitalUsd: totalCap,
        roiOnCapitalPct: roiCap,
        aprPct: apr,
        liquidationPriceUsd: liqPx,
        warnings: shortWarnings.length > 0 ? shortWarnings : undefined,
      };
      if (!bestApr || (r2.aprPct ?? 0) > (bestApr.aprPct ?? 0)) bestApr = r2;
    }
  }
  return { noShort: bestNet, withShort: bestApr };
}

export function bestScenarioNet(arb: ScenarioPair): number | null {
  const c = [arb.noShort?.netUsd, arb.withShort?.netUsd].filter((v): v is number => v != null && Number.isFinite(v));
  return c.length > 0 ? Math.max(...c) : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Глубина стакана (VWAP)
// ─────────────────────────────────────────────────────────────────────────────

/** Уровень стакана: [цена в USD, объём в базовой монете]. */
export type BookLevel = [number, number];

export interface DepthArbResult {
  qty: number;
  buyVwap: number;
  sellVwap: number;
  grossUsd: number;
  feesUsd: number;
  netUsd: number;
}

/**
 * Проходит по стаканам покупки (asks) и продажи (bids) уровень за уровнем и набирает объём,
 * пока МАРЖИНАЛЬНАЯ прибыль (bid − ask − комиссии) положительна и не превышен maxQty.
 * Возвращает реальный исполнимый объём и средние цены вместо «верхний уровень × мин. размер».
 */
export function walkBooksForArb(
  asks: BookLevel[],
  bids: BookLevel[],
  feePerUnit: (buyPx: number, sellPx: number) => number,
  maxQty = Infinity,
): DepthArbResult {
  const a = asks.filter(([p, s]) => p > 0 && s > 0).sort((x, y) => x[0] - y[0]).map(([p, s]) => [p, s] as BookLevel);
  const b = bids.filter(([p, s]) => p > 0 && s > 0).sort((x, y) => y[0] - x[0]).map(([p, s]) => [p, s] as BookLevel);
  let i = 0, j = 0, qty = 0, cost = 0, proceeds = 0, fees = 0;
  const EPS = 1e-12;
  while (i < a.length && j < b.length && qty < maxQty - EPS) {
    const [ap, as] = a[i];
    const [bp, bs] = b[j];
    const unitFee = feePerUnit(ap, bp);
    if (bp - ap - unitFee <= 0) break;
    const take = Math.min(as, bs, maxQty - qty);
    qty += take;
    cost += take * ap;
    proceeds += take * bp;
    fees += take * unitFee;
    a[i] = [ap, as - take];
    b[j] = [bp, bs - take];
    if (a[i][1] <= EPS) i++;
    if (b[j][1] <= EPS) j++;
  }
  return {
    qty,
    buyVwap: qty > 0 ? cost / qty : 0,
    sellVwap: qty > 0 ? proceeds / qty : 0,
    grossUsd: proceeds - cost,
    feesUsd: fees,
    netUsd: proceeds - cost - fees,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Капитал под сделку (общая логика бота и UI)
// ─────────────────────────────────────────────────────────────────────────────

export const HEDGE_EXCHANGE = 'weex';

export interface CapitalOptions {
  /** Хедж дельты фьючерсом. Для лонга и шорта ОДНОГО опциона дельта уже нейтральна — по умолчанию выключен. */
  hedgeEnabled: boolean;
  leverage: number;
}

export interface CapitalRequirement {
  minRequiredUsd: number;
  perExchangeRequirement: Record<string, number>;
  hedgeRequirement: { exchange: string; usd: number; leverage: number } | null;
  profitPer100Usd: number;
}

export function computeCapitalRequirement(
  s: Pick<ScenarioResult, 'buyExch' | 'sellExch' | 'buyAsk' | 'sellBid' | 'qtyBase' | 'netUsd' | 'feesUsd' | 'premiumLockUsd' | 'marginLockUsd'>,
  opts: CapitalOptions,
): CapitalRequirement {
  const per: Record<string, number> = {};
  const add = (exchange: string | null | undefined, usd: number | null | undefined): void => {
    if (!exchange || usd == null || !Number.isFinite(usd) || usd <= 0) return;
    per[exchange] = (per[exchange] ?? 0) + usd;
  };
  add(s.buyExch, s.premiumLockUsd ?? s.buyAsk * s.qtyBase);
  add(s.sellExch, s.marginLockUsd ?? 0);
  add(s.buyExch, Math.max(0, s.feesUsd));
  let hedge: CapitalRequirement['hedgeRequirement'] = null;
  if (opts.hedgeEnabled && s.buyExch && s.sellExch && s.buyExch !== s.sellExch) {
    const leverage = Math.max(1, opts.leverage);
    const notional = Math.abs(s.qtyBase) * Math.max(s.buyAsk, s.sellBid, 0);
    hedge = { exchange: HEDGE_EXCHANGE, usd: notional / leverage, leverage };
    add(hedge.exchange, hedge.usd);
  }
  const minRequiredUsd = Object.values(per).reduce((sum, v) => sum + v, 0);
  return {
    minRequiredUsd,
    perExchangeRequirement: per,
    hedgeRequirement: hedge,
    profitPer100Usd: minRequiredUsd > 0 ? (s.netUsd / minRequiredUsd) * 100 : 0,
  };
}

export function leverageForBase(base: string, cfg: { btc?: number | null; eth?: number | null; default?: number | null }): number {
  const b = base.toUpperCase();
  if (b === 'BTC') return Math.max(1, Number(cfg.btc) || 5);
  if (b === 'ETH') return Math.max(1, Number(cfg.eth) || 3);
  return Math.max(1, Number(cfg.default) || 2);
}

// ─────────────────────────────────────────────────────────────────────────────
// Лимиты риска Theta
// ─────────────────────────────────────────────────────────────────────────────

export interface RiskLimits {
  maxOpenPositions: number;      // 0 = без лимита
  maxPositionUsd: number;        // 0 = без лимита
  maxTotalExposureUsd: number;   // 0 = без лимита
  minHoursToExpiry: number;      // 0 = без лимита
  maxPerExpiry: number;          // 0 = без лимита
}

export interface OpenExposure {
  symbol: string;
  requiredUsd: number;
}

/** Возвращает причину отказа или null, если сделка укладывается в лимиты. */
export function checkRiskLimits(
  candidate: { symbol: string; minRequiredUsd: number },
  open: OpenExposure[],
  limits: RiskLimits,
  nowMs: number,
): string | null {
  if (limits.maxOpenPositions > 0 && open.length >= limits.maxOpenPositions) {
    return `open positions ${open.length} >= limit ${limits.maxOpenPositions}`;
  }
  if (limits.maxPositionUsd > 0 && candidate.minRequiredUsd > limits.maxPositionUsd) {
    return `position $${candidate.minRequiredUsd.toFixed(2)} > limit $${limits.maxPositionUsd}`;
  }
  const exposure = open.reduce((s, o) => s + (Number.isFinite(o.requiredUsd) ? o.requiredUsd : 0), 0);
  if (limits.maxTotalExposureUsd > 0 && exposure + candidate.minRequiredUsd > limits.maxTotalExposureUsd) {
    return `total exposure $${(exposure + candidate.minRequiredUsd).toFixed(2)} > limit $${limits.maxTotalExposureUsd}`;
  }
  const parsed = parseCanonical(candidate.symbol);
  if (parsed && limits.minHoursToExpiry > 0) {
    const hours = (expiryMs(parsed.expiry) - nowMs) / 3_600_000;
    if (hours < limits.minHoursToExpiry) return `${hours.toFixed(1)}h to expiry < limit ${limits.minHoursToExpiry}h`;
  }
  if (parsed && limits.maxPerExpiry > 0) {
    const sameExpiry = open.filter((o) => parseCanonical(o.symbol)?.expiry === parsed.expiry).length;
    if (sameExpiry >= limits.maxPerExpiry) return `positions on expiry ${parsed.expiry}: ${sameExpiry} >= limit ${limits.maxPerExpiry}`;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Маржа открытого шорта
// ─────────────────────────────────────────────────────────────────────────────

export const MARGIN_WARN_RATIO = 0.8;
export const MARGIN_CLOSE_RATIO = 1.0;

/**
 * Отношение текущего требования маржи шорта к заблокированной при открытии марже.
 * ≥ 0.8 — предупредить, ≥ 1.0 — закрыть позицию (иначе биржа ликвидирует шорт,
 * а лонг на другой бирже останется открытым).
 */
export function shortMarginRatio(
  optType: 'C' | 'P',
  spotNow: number,
  strike: number,
  markPremium: number,
  qty: number,
  lockedUsd: number,
  cfg?: FeeConfig,
): number | null {
  if (!isPos(spotNow) || !isPos(qty) || !isPos(lockedUsd)) return null;
  const required = shortMarginPerUnitUsd(optType, spotNow, strike, Math.max(markPremium, 0), cfg) * qty;
  return required / lockedUsd;
}
